// Reusable utility for step navigation and state
// Supports typed step IDs and clamps navigation to valid range.

import { StepId } from "../adapaters/financialStatementMeta";

export interface StepNavigator<Id extends StepId = StepId> {
  stepOrder: Id[];
  activeStepIndex: number;
  activeStepId: Id;
  goToStep: (idx: number) => void;
  nextStep: () => void;
  prevStep: () => void;
}

/**
 * Creates a step navigation helper.
 *
 * @param params.stepOrder Ordered list of step identifiers
 * @param params.currentStep Current step identifier (from URL/route)
 * @param params.navigate Function that accepts a pathname string or object compatible with router
 * @param params.basePath Base path to prefix before the step segment (e.g., "/sfs")
 */
export function createStepNavigator<Id extends StepId = StepId>(params: {
  stepOrder: Id[];
  currentStep?: Id | null;
  basePath: string;
  navigate: (to: { pathname: string } | string) => void;
}): StepNavigator<Id> {
  const { stepOrder, currentStep, navigate, basePath } = params;

  // Ensure basePath has no trailing slash
  const normalizedBase = basePath.endsWith("/")
    ? basePath.slice(0, -1)
    : basePath;

  const idx = Math.max(
    0,
    currentStep ? stepOrder.indexOf(currentStep) : 0,
  );
  const activeStepIndex = idx === -1 ? 0 : idx;
  const activeStepId = (stepOrder[activeStepIndex] ?? stepOrder[0]);

  const clamp = (n: number) => Math.min(Math.max(n, 0), stepOrder.length - 1);

  const goToStep = (index: number) => {
    const target = stepOrder[clamp(index)];
    navigate({ pathname: `${normalizedBase}/${String(target)}` });
  };

  const nextStep = () => goToStep(activeStepIndex + 1);
  const prevStep = () => goToStep(activeStepIndex - 1);

  return {
    stepOrder,
    activeStepIndex,
    activeStepId,
    goToStep,
    nextStep,
    prevStep,
  };
}
